import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { useInertBackground } from './useInertBackground';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  closeLabel: string;
  children: ReactNode;
  /** Die Hauptaktion steht hier und bleibt ohne Scrollen erreichbar (FRONTEND_GUIDELINES §8). */
  footer?: ReactNode;
  /**
   * Statt `footer`: der Fuß als Ziel, in das Bauteile im Inhalt ihre Aktion rendern — der
   * Bezahlknopf braucht den Stripe-Kontext des Inhalts. Ohne Inhalt bleibt der Fuß unsichtbar.
   */
  footerSlot?: (element: HTMLDivElement | null) => void;
  /**
   * `false` für Fenster mit Kartenzahlung: ohne Fokusfalle, die Seite dahinter wird stattdessen
   * `inert` (`useInertBackground`). Ein Klick daneben schließt dann nicht — nur Kreuz und Escape.
   */
  modal?: boolean;
}

/**
 * Das Fenster des Gastportals: am Handy von unten, ab Tablet als Dialog in der Mitte. Kopf und Fuß
 * stehen fest, nur der Inhalt scrollt; die Höhe hängt an `dvh`, weil mobile Browserleisten `vh`
 * nicht mitändern. Fokusfalle und Escape liefert Radix; den Rücksprung zum Auslöser merkt sich das
 * Fenster selbst — es hat keinen Radix-Trigger, und ohne ihn landete der Fokus auf dem Seitenkörper.
 *
 * Mittig über `inset-0` und `m-auto`, nicht über `translate`: ein verschobenes Fenster wird zum
 * Bezugsrahmen für `position: fixed`, und ein Vollbild darin (Fotos) bliebe in seinen Rand gesperrt.
 */
export function GuestSheet({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  children,
  footer,
  footerSlot,
  modal = true,
}: Props) {
  // Beim Öffnen gemerkt, noch bevor Radix den Fokus ins Fenster holt.
  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current) {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  wasOpen.current = open;
  useInertBackground(open && !modal);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <Dialog.Portal>
        {modal ? (
          <Dialog.Overlay className={OVERLAY} />
        ) : (
          // Ohne Modalität rendert Radix kein Overlay; der Grund bleibt trotzdem abgedunkelt.
          <div aria-hidden className={OVERLAY} />
        )}
        <Dialog.Content
          // Ohne Beschreibung ausdrücklich `undefined`, sonst warnt Radix; mit ihr verdrahtet Radix
          // die Beschreibung selbst.
          {...(description ? {} : { 'aria-describedby': undefined })}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            opener.current?.focus();
          }}
          onInteractOutside={modal ? undefined : (event) => event.preventDefault()}
          // `guest-portal` auch am Fenster selbst: es hängt am Body, und in der Admin-Vorschau liegt
          // der Gast-Stil nur am Telefonrahmen — dort bekam das Fenster sonst die Admin-Palette.
          className="guest-portal bg-bg-elev text-fg shadow-guest-float rounded-t-guest-sheet md:rounded-guest-sheet fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col pb-[env(safe-area-inset-bottom)] font-sans motion-safe:animate-[guestSheetUp_380ms_var(--ease-brand)] md:inset-0 md:m-auto md:h-fit md:max-h-[88dvh] md:w-[min(720px,calc(100vw-48px))] md:pb-0 md:motion-safe:animate-[guestDialogIn_380ms_var(--ease-brand)]"
        >
          <div className="shrink-0 px-4 pt-2 md:px-6 md:pt-5">
            <span
              aria-hidden
              className="bg-line-strong mx-auto mb-2 block h-1.5 w-11 rounded-full md:hidden"
            />
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <Dialog.Title className="font-display text-fg text-xl font-bold">
                  {title}
                </Dialog.Title>
                {description ? (
                  <Dialog.Description className="text-fg-dim text-sm">
                    {description}
                  </Dialog.Description>
                ) : null}
              </div>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label={closeLabel}
                  className="bg-bg-soft text-fg hover:bg-line flex size-11 shrink-0 items-center justify-center rounded-full transition-colors"
                >
                  <X className="size-4" aria-hidden />
                </button>
              </Dialog.Close>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6">{children}</div>
          {footerSlot ? (
            <div ref={footerSlot} className={`${FOOTER} empty:hidden`} />
          ) : footer ? (
            <div className={FOOTER}>{footer}</div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const OVERLAY = 'fixed inset-0 z-50 bg-black/40 motion-safe:animate-[guestFadeIn_180ms_ease-out]';
const FOOTER = 'border-line shrink-0 border-t px-4 py-3 md:px-6 md:py-4';
